import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';
import CustomAlert from '../components/common/CustomAlert';

interface AlertContextType {
  showAlert: (message: string) => void;
  showConfirm: (message: string) => Promise<boolean>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [isConfirmMode, setIsConfirmMode] = useState(false);
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);

  const showAlert = (message: string) => {
    setIsConfirmMode(false);
    setAlertMessage(message);
  };

  const showConfirm = (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setIsConfirmMode(true);
      setAlertMessage(message);
    });
  };

  const closeAlert = () => {
    if (isConfirmMode && confirmResolveRef.current) {
      confirmResolveRef.current(true);
      confirmResolveRef.current = null;
    }
    setAlertMessage(null);
    setIsConfirmMode(false);
  };

  const cancelAlert = () => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false);
      confirmResolveRef.current = null;
    }
    setAlertMessage(null);
    setIsConfirmMode(false);
  };

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {alertMessage && (
        <CustomAlert
          message={alertMessage}
          onClose={closeAlert}
          isConfirm={isConfirmMode}
          onCancel={cancelAlert}
        />
      )}
    </AlertContext.Provider>
  );
};
